import { useState, useEffect, useCallback } from 'react';
import { confidentialAPI } from '../services/api';
import { keepLatestIdCardPerSide, normalizeDocument } from '../config/documentTypes';

/**
 * Custom hook for fetching and managing all document approvals
 * 
 * Automatically handles:
 * - Fetching from unified backend endpoint
 * - Normalizing documents using configuration
 * - Error handling per document type
 * - Loading states
 * 
 * @param {Object} options - Hook options
 * @param {boolean} options.enabled - Whether to fetch documents (default: true)
 * @param {string|null} options.status - Filter by status (pending, submitted, approved, etc.)
 * @param {string|null} options.documentType - Filter by specific document type
 * @returns {Object} { documents, loading, error, refetch }
 */
export const useDocuments = (options = {}) => {
  const { 
    enabled = true,
    status = null,
    documentType = null 
  } = options;
  
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchDocuments = useCallback(async () => {
    if (!enabled) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Build query params
      const params = {};
      if (status) params.status = status;
      if (documentType) params.document_type = documentType;
      
      // Fetch from unified endpoint
      const response = await confidentialAPI.getAllDocumentApprovals(params);
      const data = response.data;
      
      // Use flat 'all' array from response
      const rawDocuments = data.all || [];
      
      // Normalize all documents using configuration
      const normalized = keepLatestIdCardPerSide(
        rawDocuments
          .map(normalizeDocument)
          .filter(Boolean)
      );
      
      // Sort by upload date (most recent first)
      normalized.sort((a, b) => {
        const ta = a.uploadedAt ? new Date(a.uploadedAt).getTime() : 0;
        const tb = b.uploadedAt ? new Date(b.uploadedAt).getTime() : 0;
        return tb - ta;
      });
      
      setDocuments(normalized);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
      setError(err);
      setDocuments([]); // Reset on error
    } finally {
      setLoading(false);
    }
  }, [enabled, status, documentType]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  return {
    documents,
    loading,
    error,
    refetch: fetchDocuments
  };
};

